import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Phone, CalendarDays, CheckSquare, Pencil, Trash2, User, FileText, Filter, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ActivityDetailDialog from "@/components/ActivityDetailDialog";
import { fmtDateTime } from "@/lib/constants";

const EMPTY = { type: "task", title: "", description: "", due_at: "", deal_id: "none", company_id: null, contact_id: null, completed: false, priority: "medium", assigned_to: null };
const ICON = { call: Phone, meeting: CalendarDays, task: CheckSquare };
const PR_COLOR = { high: "border-rose-300 bg-rose-50 text-rose-700", medium: "border-amber-300 bg-amber-50 text-amber-800", low: "border-slate-300 bg-slate-50 text-slate-600" };

// Human-friendly "3d 4h" style duration between two ISO strings.
function fmtDuration(fromISO, toISO) {
  if (!fromISO || !toISO) return "—";
  const ms = new Date(toISO).getTime() - new Date(fromISO).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  return `${days}d`;
}

// Default the completed_to filter to tomorrow so today's completions are captured.
function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Activities() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [tab, setTab] = useState("open"); // "open" | "completed"
  const [filter, setFilter] = useState("all"); // type filter (open tab)
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  // Completed tab filters
  const today = new Date();
  const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(today.getDate() - 30);
  const [compFrom, setCompFrom] = useState(isoDate(thirtyDaysAgo));
  const [compTo, setCompTo] = useState(isoDate(new Date(today.getTime() + 86400000)));
  const [compAssignee, setCompAssignee] = useState("all");

  const { data: activities = [] } = useQuery({
    queryKey: ["activities", tab, filter],
    queryFn: async () => {
      if (tab === "completed") return [];
      const params = { completed: false };
      if (filter !== "all") params.type = filter;
      return (await api.get("/activities", { params })).data;
    },
  });

  const { data: completedRaw = [] } = useQuery({
    queryKey: ["activities-completed", compFrom, compTo, compAssignee],
    queryFn: async () => {
      const params = { completed: true, limit: 500 };
      if (compFrom) params.completed_from = `${compFrom}T00:00:00`;
      if (compTo) params.completed_to = `${compTo}T00:00:00`;
      if (compAssignee !== "all") params.assignee = compAssignee;
      return (await api.get("/activities", { params })).data;
    },
    enabled: tab === "completed",
  });

  const { data: deals = [] } = useQuery({ queryKey: ["deals-all"], queryFn: async () => (await api.get("/deals")).data });
  const { data: companies = [] } = useQuery({ queryKey: ["companies-all-act"], queryFn: async () => (await api.get("/companies")).data });
  const { data: team = { users: [] } } = useQuery({ queryKey: ["team-picker-completed"], queryFn: async () => (await api.get("/auth/team")).data });
  const teamOptions = (team.users || []).filter((u) => u.status === "approved" || u.role === "super_admin");
  const coMap = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  const dealMap = Object.fromEntries(deals.map((d) => [d.id, d]));

  const toggle = useMutation({
    mutationFn: async ({ id, completed }) => (await api.patch(`/activities/${id}`, { completed })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["activities-completed"] });
    },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/activities/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["activities-completed"] });
    },
  });

  // Completed rollup — total count + by-person breakdown for the filtered range.
  const completedStats = useMemo(() => {
    const byPerson = new Map();
    for (const a of completedRaw) {
      const k = a.completed_by_name || a.assigned_to_name || "Unassigned";
      byPerson.set(k, (byPerson.get(k) || 0) + 1);
    }
    return { total: completedRaw.length, byPerson: [...byPerson.entries()].sort((a, b) => b[1] - a[1]) };
  }, [completedRaw]);

  return (
    <div>
      <PageHeader
        title="Activities"
        subtitle="Calls, meetings & tasks linked to deals. Toggle Completed to review who finished what and when."
        breadcrumb={["Sales", "Activities"]}
        actions={
          <Button data-testid="new-activity-btn" onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New activity
          </Button>
        }
      />
      <div className="px-4 md:px-6 lg:px-10 py-4 md:py-6">
        {/* Iter-74: Open / Completed tab switch. Completed tab exposes date range + assignee filters. */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button data-testid="tab-open" onClick={() => setTab("open")}
            className={`px-4 h-9 text-xs font-semibold uppercase tracking-wider border rounded-sm ${tab === "open" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
            Open
          </button>
          <button data-testid="tab-completed" onClick={() => setTab("completed")}
            className={`px-4 h-9 text-xs font-semibold uppercase tracking-wider border rounded-sm ${tab === "completed" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
            Completed
          </button>
          <div className="w-px bg-slate-200 mx-1 hidden sm:block" />
          {tab === "open" && ["all", "call", "meeting", "task"].map((t) => (
            <button key={t} data-testid={`activity-filter-${t}`} onClick={() => setFilter(t)}
              className={`px-3 h-9 text-xs font-semibold uppercase tracking-wider border rounded-sm transition-colors ${filter === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "completed" && (
          <>
            {/* Completed tab filter bar */}
            <Card className="rounded-sm border-emerald-200 bg-emerald-50/40 industrial-shadow p-3 mb-4" data-testid="completed-filter-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 stamp-label text-emerald-800 shrink-0"><Filter size={12} /> Filter</div>
                <div className="flex items-center gap-2 flex-wrap flex-1">
                  <div>
                    <Label className="text-[10px] text-emerald-700">Completed from</Label>
                    <Input type="date" value={compFrom} onChange={(e) => setCompFrom(e.target.value)}
                      className="h-9 rounded-sm bg-white font-mono w-40" data-testid="completed-from" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-emerald-700">to</Label>
                    <Input type="date" value={compTo} onChange={(e) => setCompTo(e.target.value)}
                      className="h-9 rounded-sm bg-white font-mono w-40" data-testid="completed-to" />
                  </div>
                  <div className="min-w-[180px]">
                    <Label className="text-[10px] text-emerald-700">Assigned to</Label>
                    <Select value={compAssignee} onValueChange={setCompAssignee}>
                      <SelectTrigger className="h-9 rounded-sm bg-white" data-testid="completed-assignee"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Anyone</SelectItem>
                        <SelectItem value="me">Me only</SelectItem>
                        {teamOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setCompFrom(isoDate(thirtyDaysAgo)); setCompTo(isoDate(new Date(today.getTime() + 86400000))); setCompAssignee("all"); }}
                    className="h-9 rounded-sm text-xs text-emerald-700 hover:bg-emerald-100 mt-4"
                    data-testid="completed-reset-filters">
                    <RotateCcw size={12} className="mr-1" /> Reset
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-emerald-200 text-xs text-emerald-900 flex-wrap" data-testid="completed-stats">
                <div><b className="font-mono text-lg">{completedStats.total}</b> completed in range</div>
                {completedStats.byPerson.slice(0, 5).map(([who, n]) => (
                  <Badge key={who} variant="outline" className="rounded-sm border-emerald-300 bg-white text-emerald-800">{who}: {n}</Badge>
                ))}
                {completedStats.byPerson.length > 5 && <span className="text-emerald-600 text-[10px]">+{completedStats.byPerson.length - 5} more</span>}
              </div>
            </Card>

            {/* Completed table (mobile card fallback below md) */}
            <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="completed-list">
              {/* Mobile card list */}
              <div className="md:hidden divide-y divide-slate-100">
                {completedRaw.map((a) => {
                  const Icon = ICON[a.type] || CheckSquare;
                  return (
                    <div key={a.id} className="p-3 hover:bg-emerald-50/30" data-testid={`completed-mob-${a.id}`}
                      onClick={() => { setEditing({ ...a }); setOpen(true); }}>
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 text-emerald-700 rounded-sm shrink-0">
                          <Icon size={13} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">Done {a.completed_at ? fmtDateTime(a.completed_at) : "—"} by {a.completed_by_name || "—"}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Assigned to {a.assigned_to_name || "Unassigned"}
                            {a.due_at && <> · due was {fmtDateTime(a.due_at)}</>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {completedRaw.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No completed tasks in this range</div>}
              </div>
              <table className="w-full text-sm hidden md:table" data-testid="completed-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left stamp-label text-slate-500">
                    <th className="px-4 py-3">Task</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Completed at</th>
                    <th className="px-4 py-3">Completed by</th>
                    <th className="px-4 py-3">Assigned to</th>
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Time to complete</th>
                    <th className="px-4 py-3 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {completedRaw.map((a) => (
                    <tr key={a.id} className="hover:bg-emerald-50/30 cursor-pointer" data-testid={`completed-row-${a.id}`}
                      onClick={() => { setEditing({ ...a }); setOpen(true); }}>
                      <td className="px-4 py-2.5 font-medium text-slate-900">
                        <div className="line-through text-slate-600 truncate max-w-[280px]">{a.title}</div>
                        {a.deal_id && dealMap[a.deal_id]?.job_number && (
                          <div className="text-[10px] font-mono text-cyan-700">{dealMap[a.deal_id].job_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="rounded-sm text-[10px] capitalize border-slate-300">{a.type}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs font-mono">{a.completed_at ? fmtDateTime(a.completed_at) : "—"}</td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs">{a.completed_by_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs">{a.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {a.company_id ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/clients/${a.company_id}`); }}
                            className="text-cyan-700 hover:underline">{coMap[a.company_id] || "View"}</button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 hidden lg:table-cell text-xs font-mono">{fmtDuration(a.due_at || a.created_at, a.completed_at)}</td>
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => toggle.mutate({ id: a.id, completed: false })}
                          title="Reopen task" data-testid={`completed-reopen-${a.id}`}>
                          <RotateCcw size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {completedRaw.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">No completed tasks in this range</td></tr>}
                </tbody>
              </table>
            </Card>
          </>
        )}

        {tab === "open" && (
          <Card className="rounded-sm border-slate-200 industrial-shadow divide-y divide-slate-100">
            {activities.map((a) => {
              const Icon = ICON[a.type] || CheckSquare;
              return (
                <div key={a.id} className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-cyan-50/30 ${a.completed ? "opacity-60" : ""}`} data-testid={`activity-${a.id}`}>
                  <Checkbox checked={a.completed} onCheckedChange={(v) => toggle.mutate({ id: a.id, completed: !!v })} data-testid={`toggle-activity-${a.id}`} />
                  <div className={`hidden sm:flex w-9 h-9 items-center justify-center ${a.completed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                    <Icon size={15} />
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setEditing({ ...a }); setOpen(true); }} data-testid={`activity-open-${a.id}`}>
                    <div className="flex items-start gap-2">
                      <span className={`sm:hidden mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${a.priority === "high" ? "bg-rose-500" : a.priority === "low" ? "bg-slate-300" : "bg-amber-400"}`} aria-label={`priority ${a.priority}`} />
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-medium truncate ${a.completed ? "line-through text-slate-500" : "text-slate-900"}`} data-testid={`activity-title-${a.id}`}>
                          {a.title}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-slate-500">
                          <span className="capitalize">{a.type}</span>
                          {a.due_at && <span>· due {fmtDateTime(a.due_at)}</span>}
                          {a.deal_id && dealMap[a.deal_id]?.job_number && (
                            <span className="font-mono text-cyan-700" data-testid={`activity-jn-${a.id}`}>{dealMap[a.deal_id].job_number}</span>
                          )}
                          {a.assigned_to_name && <span className="text-slate-400">→ {a.assigned_to_name}</span>}
                          {a.company_id && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/clients/${a.company_id}`); }}
                              className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
                              data-testid={`activity-client-link-${a.id}`}>
                              <User size={10} /> {coMap[a.company_id] || "View client"}
                            </button>
                          )}
                          {a.quote_id && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/quotes/v2/${a.quote_id}`); }}
                              className="inline-flex items-center gap-1 text-violet-700 hover:underline"
                              data-testid={`activity-quote-link-${a.id}`}>
                              <FileText size={10} /> View quote
                            </button>
                          )}
                          {a.auto_generated && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-200 bg-cyan-50 text-cyan-700">AUTO</Badge>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`hidden sm:inline-flex rounded-sm text-[10px] shrink-0 ${PR_COLOR[a.priority] || ""}`}>{a.priority}</Badge>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => { setEditing({ ...a }); setOpen(true); }} data-testid={`activity-edit-${a.id}`}><Pencil size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600 shrink-0" onClick={() => del.mutate(a.id)} data-testid={`activity-del-${a.id}`}><Trash2 size={13} /></Button>
                </div>
              );
            })}
            {activities.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No activities</div>}
          </Card>
        )}
      </div>

      <ActivityDetailDialog activity={editing} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
